
import { useEffect, useState, useRef, useCallback } from "react";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { fetchInvestementPage } from "../../services/api";

const HowWeInvest = ({ lang }) => {
  const [content, setContent] = useState(null);
  const [textVisible, setTextVisible] = useState(false);
  const [inView, setInView] = useState(false);
  const sectionRef = useRef(null);
  const desktopLottieRef = useRef(null);
  const mobileLottieRef = useRef(null);
  const hasPlayed = useRef(false);

  useEffect(() => {
    setContent(null);
    setTextVisible(false);
    setInView(false);
    hasPlayed.current = false;

    const loadData = async () => {
      try {
        const enData = await fetchInvestementPage("en");
        if (lang !== "en") {
          const localData = await fetchInvestementPage(lang);
          const hasLocalData =
            localData &&
            !Array.isArray(localData) &&
            Object.keys(localData).length > 0;
          setContent(hasLocalData ? { ...enData, ...localData } : enData);
        } else {
          setContent(enData);
        }
      } catch {
        const fallback = await fetchInvestementPage("en");
        setContent(fallback);
      }
    };

    loadData();
  }, [lang]);

  // IntersectionObserver — sets inView flag
  useEffect(() => {
    if (!content || !sectionRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          setTextVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );

    observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, [content]);

  // Play animation — only once
  const tryPlay = useCallback(() => {
    if (!inView || hasPlayed.current) return;

    const desktop = desktopLottieRef.current;
    const mobile = mobileLottieRef.current;

    if (!desktop && !mobile) return;

    hasPlayed.current = true;

    if (desktop) {
      desktop.stop?.();
      desktop.play?.();
    }
    if (mobile) {
      mobile.stop?.();
      mobile.play?.();
    }
  }, [inView]);

  // Retry play when inView becomes true
  useEffect(() => {
    if (!inView) return;

    const t1 = setTimeout(tryPlay, 100);
    const t2 = setTimeout(tryPlay, 400);
    const t3 = setTimeout(tryPlay, 800);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [inView, tryPlay]);

  if (!content) return <p className="p-6">Loading...</p>;

  const circles =
    lang === "ja"
      ? [
          {
            title: (<>オペレーター<br />コンテキスト</>),
            desc: (<>経験に基づいた<br />的確な判断力</>),
          },
          {
            title: (<>長期的な<br />パートナーシップ</>),
            desc: (<>機関投資家の支援による<br />忍耐強いエクイティ</>),
          },
          {
            title: (<>マンデートの<br />自律性</>),
            desc: (<>独立して行動する<br />自由</>),
          },
        ]
      : [
          {
            title: (<>Operator <br /> Context</>),
            desc: (<>Grounded, experience-<br />led judgement</>),
          },
          {
            title: (<>Long-Term<br /> Partnership</>),
            desc: (<>Patient equity with <br /> institutional backing</>),
          },
          {
            title: (<>Mandate<br /> Autonomy</>),
            desc: (<>Freedom to act <br /> independently</>),
          },
        ];

  const circleOverlay = (
    <div className="absolute inset-0 flex flex-wrap items-center circle-text-fade">
      {circles.map((circle, i) => (
        <div
          key={i}
          className="w-[100%] md:w-[33.33%] flex flex-col px-8 py-4"
          style={{
            opacity: textVisible ? 1 : 0,
            transform: textVisible ? "translateY(0)" : "translateY(20px)",
            transition: `opacity 0.8s ease ${i * 0.2}s, transform 0.8s ease ${i * 0.2}s`,
          }}
        >
          <h4 className="size-h3 font-bold text-dark-blue-color">
            {circle.title}
          </h4>
          <p className="text-dark-blue-color">{circle.desc}</p>
        </div>
      ))}
    </div>
  );

  return (
    <section
      ref={sectionRef}
      style={{ backgroundColor: "rgba(188, 226, 245, 1)" }}
      className="padding-x-40 pt-[80px] pb-[40px]"
    >
      <div className="section-name-wrapper">
        <div className="section-name-dot"></div>
        <h6 className="section-name text-dark-blue-color">
          {content.How_We_Invest_Section_Name}
        </h6>
      </div>

      <div className="flex flex-wrap">
        <h3 className="size-h3 w-[100%] md:w-[65%] pt-2.5 text-dark-blue-color">
          {content.How_We_Invest_Heading}
        </h3>
        <p className="w-[100%] lg:w-[33.33%] lg:pl-10 size-h5 pt-2.5 ml-auto text-dark-blue-color">
          {content.How_We_Invest_Description}
        </p>
      </div>

      {/* Desktop */}
      <div style={{ minHeight: "400px" }} className="relative hidden md:block">
        <DotLottieReact
          src="/lottie-files/How-we-invest.lottie"
          loop={false}
          autoplay={false}
          dotLottieRefCallback={(ref) => {
            desktopLottieRef.current = ref;
            if (ref && inView && !hasPlayed.current) {
              hasPlayed.current = true;
              setTimeout(() => ref.play?.(), 100);
            }
          }}
        />
        {circleOverlay}
      </div>

      {/* Mobile */}
      <div
        style={{ minHeight: "400px", overflowX: "hidden" }}
        className="relative block md:hidden"
      >
        <DotLottieReact
          src="/lottie-files/How-we-invest.lottie"
          loop={false}
          autoplay={false}
          dotLottieRefCallback={(ref) => {
            mobileLottieRef.current = ref;
            if (ref && inView && !hasPlayed.current) {
              hasPlayed.current = true;
              setTimeout(() => ref.play?.(), 100);
            }
          }}
          style={{ transform: "rotate(90deg) scale(2)" }}
        />
        {circleOverlay}
      </div>
    </section>
  );
};

export default HowWeInvest;
